import { BasePlugin } from '@uppy/core';
import axios from 'axios';
import * as tus from 'tus-js-client';
import Uppy from '@uppy/core';
import Dashboard from '@uppy/dashboard';
import ImageEditor from '@uppy/image-editor';
import Compressor from '@uppy/compressor';

import { styles_plugin } from './styles-plugin.js';

class CustomUploaderPlugin extends BasePlugin {
    constructor(uppy, opts) {
        super(uppy, opts);
        this.id = opts.id || 'CustomUploaderPlugin';
        this.type = 'uploader';

        this.uploadFile = this.uploadFile.bind(this);
        this.abortUpload = this.abortUpload.bind(this);
        this.uploaders = {}; // Store upload instances
    }

    async getUploadUrl(baseUrl, mimeType, options, plan, link, total, index) {
        const response = await fetch(baseUrl + '/_functions/@purple/image-uploader-backend/generateUploadUrlPlugin', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ mimeType, options, plan, link, total, index })
        });
        const data = await response.json();
        if (response.ok) {
            return data.uploadUrl;
        } else {
            throw new Error(data.error);
        }
    }

    async prepare(file) {
        const { uppy } = this;

        if (file.type.includes('video/mp4')) {
            file.type = 'video/mp4';
            file.meta.type = 'video/mp4';
        }
        if (file.progress.uploadComplete) return;

        const mimeType = file.type;
        const options = {
            fileName: file.name,
            filePath: '/Users Products Uploads/' + this.opts.randomPath,
            parentFolderId: 'media-root',
            labels: [file.name],
            private: false
        };

        try {
            const responce = await this.getUploadUrl(this.opts.baseUrl, mimeType, options, this.opts.plan, this.opts.link);
            const uploadUrl = responce.uploadUrl;
            const uploadToken = responce.uploadToken;
            const finalUploadUrl = `${uploadUrl}/${uploadToken}`;

            uppy.setFileState(file.id, { meta: { ...file.meta, uploadUrl, finalUploadUrl, uploadToken } });
        } catch (error) {
            uppy.setFileState(file.id, { meta: { ...file.meta, uploadUrl: undefined, finalUploadUrl: undefined, uploadToken: undefined, quota: error.message } });
        }
    }

    async uploadFile(fileIDs) {
        const { uppy } = this;
        const filesArr = uppy.getFiles();
        const totalFiles = filesArr.length;
        let successful = [];
        let failed = [];

        uppy.emit('upload-start', filesArr);

        for (let i = 0; i < totalFiles; i++) {
            const fileID = filesArr[i].id;
            const fileBefore = filesArr[i];

            try {
                await this.prepare(fileBefore, totalFiles, i + 1);

                const file = uppy.getFile(fileID);
                if (file.progress.uploadComplete) {
                    uppy.emit('upload-progress', file, file.progress);
                    uppy.emit('upload-success', file, file.response);
                    successful.push(file);
                    continue;
                }
                if (file.meta.quota) {
                    const error = new Error(file.meta.quota);
                    uppy.emit('upload-error', file, error);
                    uppy.info(error, 'error', 15000);
                    const progress = {
                        uploader: this,
                        bytesUploaded: 0,
                        bytesTotal: file.size
                    };
                    uppy.emit('upload-progress', file, progress);
                    failed.push(file);
                    continue;
                }

                const uploadUrl = file.meta.uploadUrl;
                const finalUploadUrl = file.meta.finalUploadUrl;
                const uploadToken = file.meta.uploadToken;

                if (!uploadUrl || !uploadToken) {
                    const error = new Error('Upload URL or Token is missing');
                    uppy.emit('upload-error', file, error);
                    failed.push(file);
                    continue;
                }

                await new Promise((resolve, reject) => {
                    const upload = new tus.Upload(file.data, {
                        endpoint: uploadUrl,
                        retryDelays: [0, 3000, 5000, 10000, 20000],
                        metadata: {
                            filename: file.name,
                            contentType: file.type,
                            token: uploadToken
                        },
                        onError: (error) => {
                            uppy.emit('upload-error', file, error);
                            uppy.log(error);
                            failed.push(file);
                            reject(error);
                        },
                        onProgress: (bytesUploaded, bytesTotal) => {
                            const progress = {
                                uploader: this,
                                bytesUploaded,
                                bytesTotal
                            };
                            uppy.emit('upload-progress', file, progress);
                        },
                        onSuccess: async () => {
                            try {
                                const result = await axios.put(finalUploadUrl, {}, { params: { filename: file.name } });
                                uppy.emit('upload-success', file, {
                                    status: result.status,
                                    body: result.data,
                                    uploadURL: result.data.file.url
                                });
                                uppy.setFileState(fileID, { progress: { uploadComplete: true, percentage: 100 } });
                                successful.push(file);
                                resolve();
                            } catch (error) {
                                uppy.emit('upload-error', file, error);
                                uppy.log(error);
                                failed.push(file);
                                reject(error);
                            }
                        }
                    });

                    this.uploaders[fileID] = upload;
                    upload.start();
                });
            } catch (error) {
                failed.push(fileBefore);
            }
        }

        const freshFilesArr = uppy.getFiles();
        successful = freshFilesArr.filter(file => file.progress.uploadComplete);
        failed = freshFilesArr.filter(file => !file.progress.uploadComplete);

        uppy.emit('complete', { successful, failed });
    }

    abortUpload(fileID) {
        const { uppy } = this;
        const file = uppy.getFile(fileID);

        if (this.uploaders[fileID]) {
            this.uploaders[fileID].abort();
            uppy.emit('upload-cancel', file);
            uppy.setFileState(fileID, { progress: { uploadComplete: false, uploadStarted: false, percentage: 0 } });
            delete this.uploaders[fileID];
        }
    }

    install() {
        this.uppy.addUploader(this.uploadFile);

        // Listen for cancel-all event
        this.uppy.on('cancel-all', () => {
            const fileIDs = Object.keys(this.uploaders);
            fileIDs.forEach(fileID => this.abortUpload(fileID));
        });

        // Listen for file-remove event
        this.uppy.on('file-removed', (file) => {
            this.abortUpload(file.id);
        });
    }

    uninstall() {
        this.uppy.removeUploader(this.uploadFile);
    }
}

export class PluginUploaderElement extends HTMLElement {
    constructor() {
        super();
        this.attachShadow({ mode: 'open' });
        this.uppy;
        this.maxFileSizes;
        this.allowedFileTypes = {
            'image': [
                'image/jpeg', 'image/png', 'image/gif', 'image/jpeg', 'image/jpe', 'image/jfif', 'image/bmp',
                'image/heic', 'image/heif', 'image/tiff', 'image/tif', 'image/webp', 'image/jp2', 'image/j2k',
                'image/jpf', 'image/jpm', 'image/j2c', 'image/jpc', 'image/jpx', 'image/arw', 'image/srw',
                'image/nef', 'image/cr2', 'image/cr3', 'image/crw', 'image/rwl', 'image/rw2', 'image/raw',
                'image/raf', 'image/pef', 'image/orf', 'image/mrw', 'image/dng', 'image/sr2', 'image/srf',
                'image/kdc', 'image/k25', 'image/dcr', 'image/x3f', 'image/erf', 'image/3fr'
            ],
            'video': [
                'video/avi', 'video/mpeg', 'video/mpg', 'video/mpe', 'video/mp4', 'video/webm', 'video/quicktime',
                'video/ogg', 'video/vob', 'video/x-m4v', 'video/3gpp', 'video/divx', 'video/x-xvid', 'video/mxf',
                'video/x-ms-wmv', 'video/x-matroska', 'video/x-flv', 'video/m2ts'
            ],
            'audio': [
                'audio/mpeg', 'audio/wav', 'audio/flac', 'audio/mp4', 'audio/x-ms-wma', 'audio/aac', 'audio/aiff', 'audio/x-aiff'
            ],
            'document': [
                'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                'application/vnd.ms-word.document.macroEnabled.12', 'application/vnd.openxmlformats-officedocument.wordprocessingml.template',
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel',
                'application/vnd.ms-excel.sheet.macroEnabled.12', 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
                'application/vnd.ms-powerpoint', 'application/pdf', 'application/vnd.ms-xpsdocument', 'application/oxps',
                'application/vnd.ms-publisher', 'text/csv', 'text/plain', 'application/rtf', 'application/json', 'application/vnd.oasis.opendocument.text',
                'application/vnd.oasis.opendocument.spreadsheet', 'application/vnd.oasis.opendocument.presentation',
                'application/vnd.oasis.opendocument.graphics', 'application/vnd.apple.pages', 'application/vnd.apple.numbers',
                'application/vnd.apple.keynote', 'text/vcard', 'text/calendar', 'application/epub+zip', 'application/x-mobipocket-ebook',
                'image/vnd.adobe.photoshop', 'application/postscript', 'application/vnd.adobe.indesign', 'application/x-shockwave-flash',
                'application/eps', 'application/x-xcf', 'application/cdr', 'application/vnd.dwg', 'application/gpx+xml', 'application/vnd.google-earth.kml+xml',
                'application/vnd.google-earth.kmz', 'application/x-ipynb+json'
            ],
            'vector': ['image/svg+xml'],
            'icons': ['image/vnd.microsoft.icon'],
            'archive': ['application/zip', 'application/x-rar-compressed', 'application/x-tar', 'application/x-gtar',
                'application/gzip', 'application/x-7z-compressed', 'application/java-archive'
            ],
            '3d': ['model/gltf+json', 'model/gltf-binary']
        };
    }

    connectedCallback() {
        this.props = JSON.parse(this.getAttribute("settings"));
        this.check_if_create();
        this.main = document.querySelector('main');
    }
    async attributeChangedCallback(name, oldValue, newValue) {
        if (this.uppy) {
            await this.uppy.destroy();
            this.uppy = null;
        }
        this.props = JSON.parse(this.getAttribute("settings"));
        setTimeout(() => {
            this.check_if_create();
        }, 300);

    }
    disconnectedCallback() {
        console.log("disconnected Callback");
        try {
            this.uppy.destroy();
            this.uppy = null;
        } catch (error) {
            console.error(error);
        }
    }
    check_if_create() {
        let textArea;
        if (this.main) textArea = this.main.querySelector('textarea[maxlength="498"]');
        else textArea = document.querySelector('textarea[maxlength="498"]');
        console.log(textArea);

        if (!textArea && this.props.viewMode !== "Editor") {
            this.style.display = "none";
            this.parentNode.style.display = "none";
            this.parentNode.parentNode.style.display = "none";
            return console.error('textarea not found');
        } else if (textArea) {
            try {
                textArea.style.display = "none";
                if (textArea.parentNode?.style) {
                    textArea.parentNode.style.display = "none";
                }
                if (textArea.parentNode?.parentNode?.style) {
                    textArea.parentNode.parentNode.style.display = "none";
                }
            } catch (error) {
                console.log(error)
            }

            setTimeout(() => {
                textArea.style.display = "none";
                textArea.parentNode.style.display = "none";
                textArea.parentNode.parentNode.style.display = "none";
            }, 400);
        }
        if (this.parentNode?.style) {
            this.parentNode.style.display = "inherit";
        }
        if (this.parentNode?.parentNode?.style?.display) {
            this.parentNode.parentNode.style.display = "inherit";
        }
        if (this.style?.display) {
            this.style.display = "inherit";
        }
        this.render();
        this.initializeUppy();
    }
    render() {
        this.shadowRoot.innerHTML = `
            ${styles_plugin}
            <div id="uppyDashboard"></div>
        `;
        if (this.props.inline) {
            if (this.parentNode?.style?.['min-height']) {
                this.parentNode.style['min-height'] = '100% !important';
            }
            if (this.parentNode?.style?.height) {
                this.parentNode.style.height = '100% !important';
            }
        }
    }
    handle_sizes() {
        this.maxFileSizes = {
            'image/*': this.props.image * 1024 * 1024, // 25 MB
            'image/gif': this.props.gif * 1024 * 1024, // 15 MB for GIFs
            'video/*': this.props.video / 1000 * 1024 * 1024 * 1024, // 4 GB
            'audio/*': this.props.audio * 1024 * 1024, // 50 MB
            'image/svg+xml': this.props.svg * 1024, // 250 KB for SVG
            'image/vnd.microsoft.icon': 25 * 1024 * 1024, // 25 MB for ICO
            'application/*': this.props.application * 1024 * 1024, // 1000 MB for documents and other media
            'model/*': this.props.model * 1024 * 1024 // 25 MB for 3D models
        };

        if (!this.props.allowImages && !this.props.allowGifs) {
            delete this.allowedFileTypes.image;
            delete this.maxFileSizes['image/*']
            delete this.maxFileSizes['image/gif']
        } else if (this.props.allowImages && !this.props.allowGifs) {
            this.allowedFileTypes.image = [
                'image/jpeg', 'image/png', 'image/jpeg', 'image/jpe', 'image/jfif', 'image/bmp',
                'image/heic', 'image/heif', 'image/tiff', 'image/tif', 'image/webp', 'image/jp2', 'image/j2k',
                'image/jpf', 'image/jpm', 'image/j2c', 'image/jpc', 'image/jpx', 'image/arw', 'image/srw',
                'image/nef', 'image/cr2', 'image/cr3', 'image/crw', 'image/rwl', 'image/rw2', 'image/raw',
                'image/raf', 'image/pef', 'image/orf', 'image/mrw', 'image/dng', 'image/sr2', 'image/srf',
                'image/kdc', 'image/k25', 'image/dcr', 'image/x3f', 'image/erf', 'image/3fr'
            ]
            delete this.maxFileSizes['image/gif']
        } else if (!this.props.allowImages && this.props.allowGifs) {
            this.allowedFileTypes.image = ['image/gif']
            delete this.maxFileSizes['image/*']
        }
        if (!this.props.allowVectors) {
            delete this.allowedFileTypes.vector;
            delete this.maxFileSizes['image/svg+xml']
        }
        if (!this.props.allowVideos) {
            delete this.allowedFileTypes.video;
            delete this.maxFileSizes['video/*']
        }
        if (!this.props.allowAudios) {
            delete this.allowedFileTypes.audio;
            delete this.maxFileSizes['audio/*']
        }
        if (!this.props.allowDocs) {
            delete this.allowedFileTypes.document;
            delete this.allowedFileTypes.archive;
            delete this.maxFileSizes['application/*']
        }
        if (!this.props.allow3d) {
            delete this.allowedFileTypes['3d'];
            delete this.maxFileSizes['model/*']
        }
    }
    async initializeUppy() {
        this.handle_sizes();
        this.randomPath = new Date().toLocaleDateString().replace(/\//g, '.') + "_" + window.location.pathname.split('/').filter(segment => segment.length > 0).pop() + "_" + new Date().getTime();
        console.log('this.maxFileSizes', this.maxFileSizes);
        console.log('this.allowedFileTypes', this.allowedFileTypes);
        let el = this.props.button;

        this.uppy = new Uppy({
            restrictions: {
                //maxFileSize: 4000000000, // 4000MB
                maxNumberOfFiles: this.props.maxNumberOfFiles,
                minNumberOfFiles: this.props.minNumberOfFiles,
                //allowedFileTypes: Object.values(this.allowedFileTypes).flat()
                // allowedFileTypes: ['image/*', 'video/*', 'audio/*', 'application/*']
                allowedFileTypes: Object.keys(this.maxFileSizes)
            }
        });

        this.uppy.use(Dashboard, {
            inline: this.props.inline,
            theme: this.props.theme,
            trigger: document.querySelector('#' + el),
            target: this.shadowRoot.querySelector('#uppyDashboard'),
            showProgressDetails: this.props.showProgressDetails,
            browserBackButtonClose: this.props.browserBackButtonClose,
            proudlyDisplayPoweredByUppy: false,
            note: this.props.note,
            closeModalOnClickOutside: this.props.closeModalOnClickOutside,
            closeAfterFinish: this.props.closeAfterFinish,
            disablePageScrollWhenModalOpen: this.props.disablePageScrollWhenModalOpen,
            disableStatusBar: this.props.disableStatusBar,
            showLinkToFileUploadResult: this.props.showLinkToFileUploadResult,
            hidePauseResumeButton: true,
            showSelectedFiles: this.props.showSelectedFiles,
            disableInformer: this.props.disableInformer,
            disableThumbnailGenerator: this.props.disableThumbnailGenerator
        });

        if (this.props.showImageEditor) this.uppy.use(ImageEditor, { target: Dashboard });

        if (this.props.useImageCompressor) this.uppy.use(Compressor);

        this.uppy.use(CustomUploaderPlugin, { baseUrl: this.props.baseUrl, plan: this.props.plan, link: this.props.link, randomPath: this.randomPath });

        this.uppy.on('compressor:complete', async (files) => {

            files.forEach((fileData) => {

                let file = this.uppy.getFile(fileData.id);
                const fileType = file.type;
                const fileExt = file.name.split('.').pop().toLowerCase();
                let isValid = false;
                let maxFileSize;
                for (const [type, types] of Object.entries(this.allowedFileTypes)) {
                    if (types.includes(fileType) || types.some(ext => fileExt === ext.split('/').pop())) {
                        maxFileSize = this.maxFileSizes[fileType];
                        if (!maxFileSize) {
                            for (const [wildcardType, size] of Object.entries(this.maxFileSizes)) {
                                if (wildcardType.endsWith('/*') && fileType.startsWith(wildcardType.split('/')[0])) {
                                    maxFileSize = size;
                                    break;
                                }
                            }
                        }
                        if (maxFileSize && file.size <= maxFileSize) {
                            isValid = true;
                        } else {
                            uppy.removeFile(file.id);
                            uppy.info(`File size exceeds the limit for ${type}: ${maxFileSize / (1024 * 1024)} MB`, 'error', 5000);
                        }
                    }
                }
                if (!isValid) {
                    this.uppy.removeFile(file.id);
                    this.uppy.info(`Invalid file type or size for file ${file.name}`, 'error', 5000);
                }
            })
        })

        this.uppy.on('file-removed', async (file) => {
            const filesArr = this.uppy.getFiles();
            const totalFiles = filesArr.length;
            if (totalFiles === 0) {
                this.parentNode.parentNode.style['min-height'] = 'auto';
                this.style['min-height'] = 'auto';
            }
        })
        this.uppy.on('file-added', async (file) => {
            this.parentNode.parentNode.style['min-height'] = '385px';
            this.style['min-height'] = '385px';
            //console.log(file);
            if (file.type?.includes('video/x-matroska')) {
                this.uppy.emit('upload-error', file, 'WIX does not support .mkv at the moment');
                this.uppy.info(`Error: WIX does not support .mkv at the moment`, 'error', 5000);
                return this.uppy.removeFile(file.id);
            } else if (file.type?.includes('audio/webm')) {
                let wavBlob = await this.convertWebMToWav(file.data);
                let newFile = new File([wavBlob], file.name.replace('.webm', '.wav'), { type: 'audio/wav' });
                file.type = 'audio/wav';
                file.extension = 'wav';
                file.meta.type = 'audio/wav';
                file.meta.name = file.name;
                this.uppy.setFileState(file.id, {
                    data: newFile,
                    type: newFile.type,
                    name: newFile.name,
                });
            }
            const fileType = file.type;
            const fileExt = file.name.split('.').pop().toLowerCase();
            let isValid = false;
            let maxFileSize;
            if ('image/jpeg image/webp'.includes(fileType) && this.props.useImageCompressor) return;
            for (const [type, types] of Object.entries(this.allowedFileTypes)) {
                if (types.includes(fileType) || types.some(ext => fileExt === ext.split('/').pop())) {
                    maxFileSize = this.maxFileSizes[fileType];
                    if (!maxFileSize) {
                        for (const [wildcardType, size] of Object.entries(this.maxFileSizes)) {
                            if (wildcardType.endsWith('/*') && fileType.startsWith(wildcardType.split('/')[0])) {
                                maxFileSize = size;
                                break;
                            }
                        }
                    }
                    if (maxFileSize && file.size <= maxFileSize) {
                        isValid = true;
                    } else {
                        this.uppy.removeFile(file.id);
                        this.uppy.info(`File size exceeds the limit for ${type}: ${maxFileSize / (1024 * 1024)} MB`, 'error', 5000);
                    }
                }
            }
            if (!isValid) {
                this.uppy.removeFile(file.id);
                this.uppy.info(`Invalid file type or size for file ${file.name}. File has been removed from a list.`, 'error', 5000);
            }
        });

        this.uppy.on('upload-progress', (file, progress) => {
            //console.log(`Upload progress for ${file.name}: ${progress.bytesUploaded} / ${progress.bytesTotal}`);
        });

        this.uppy.on('upload-success', (file, response) => {
            //console.log(`Upload success for ${file.name}:`, response);
        });

        this.uppy.on('complete', (result) => {
            //console.log(result);
            //console.log('Upload complete! Email Owner Sent');
            // if (result.successful.length > 0) {
            //     this.uppy.info('Upload successful!', 'success', 5000);
            // }
            //             console.log(successful);
            // console.log(failed);
            this.uppy.info(`Upload complete! Successful - ${result.successful.length}, Failed - ${result.failed.length}`, 'info', 15000);
            this.add_info();
        });

        this.uppy.on('error', (error) => {
            //console.error(error);
            this.uppy.info(`Error: ${error.message}`, 'error', 4000);
        });

    }

    add_info(FOLDER_NAME = '/Users Products Uploads/' + this.randomPath) {
        const textarea = document.querySelector('textarea[maxlength="498"]');
        if (!textarea) return console.error('textarea not found');
        textarea.value = FOLDER_NAME; // Set the textarea value directly

        // Disable the textarea
        textarea.disabled = true;

        // Find the React event handlers attached to the textarea
        const reactHandlerKey = Object.keys(textarea).find(key => key.startsWith('__reactProps$'));
        const reactProps = textarea[reactHandlerKey];

        // Create a synthetic event
        const syntheticEvent = new Event('input', { bubbles: true });

        // Trigger the React onChange handler with a plain object
        if (reactProps && reactProps.onChange) {
            reactProps.onChange({
                ...syntheticEvent,
                target: { value: FOLDER_NAME }, // Only pass the value, not the entire element
                currentTarget: { value: FOLDER_NAME }
            });
            reactProps.onBlur({target: { value: FOLDER_NAME }});
        }
    }
    async convertWebMToWav(webmBlob) {
        const arrayBuffer = await webmBlob.arrayBuffer();
        const audioContext = new(window.AudioContext || window.webkitAudioContext)();
        const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

        const wavBuffer = this.audioBufferToWav(audioBuffer);
        return new Blob([wavBuffer], { type: 'audio/wav' });
    }

    audioBufferToWav(buffer) {
        const numOfChannels = buffer.numberOfChannels;
        const length = buffer.length * numOfChannels * 2 + 44;
        const bufferArray = new ArrayBuffer(length);
        const view = new DataView(bufferArray);

        this.writeString(view, 0, 'RIFF');
        view.setUint32(4, length - 8, true);
        this.writeString(view, 8, 'WAVE');
        this.writeString(view, 12, 'fmt ');
        view.setUint32(16, 16, true);
        view.setUint16(20, 1, true);
        view.setUint16(22, numOfChannels, true);
        view.setUint32(24, buffer.sampleRate, true);
        view.setUint32(28, buffer.sampleRate * 2 * numOfChannels, true);
        view.setUint16(32, numOfChannels * 2, true);
        view.setUint16(34, 16, true);
        this.writeString(view, 36, 'data');
        view.setUint32(40, length - 44, true);

        let offset = 44;
        for (let i = 0; i < buffer.length; i++) {
            for (let channel = 0; channel < numOfChannels; channel++) {
                const sample = buffer.getChannelData(channel)[i];
                view.setInt16(offset, sample * 0x7fff, true);
                offset += 2;
            }
        }

        return bufferArray;
    }

    writeString(view, offset, string) {
        for (let i = 0; i < string.length; i++) {
            view.setUint8(offset + i, string.charCodeAt(i));
        }
    }
    static get observedAttributes() {
        return ['settings'];
    }

}

window.customElements.define('plugin-element', PluginUploaderElement);